import * as XLSX from 'xlsx';
import type { InventoryMovementsReport } from '../services/inventoryReportService';
import { fromMinorUnitsOrNull } from './money';

/**
 * REPORTING-CENTER.1 — Inventory Movements & Stock on Hand workbook.
 *
 * Variant of stockCountInventoryCycleReportTemplate.ts for the date-range
 * report: same 3-sheet layout (Summary / Movement Detail / Metadata) but the
 * summary carries Opening / period buckets / Net Change / Closing — there is
 * no physical count, so no Expected/Physical/Variance columns. All cells are
 * static values (no formulas).
 */

const numOrBlank = (n: number | null | undefined): number | '' => (n == null ? '' : n);

const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};

const fmtDateOnly = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toISOString().slice(0, 10);
};

/** Sanitize a string for safe use in a filename (spaces/specials → single dashes). */
const sanitizeFilePart = (raw: string): string =>
  raw.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const SUMMARY_HEADERS = [
  'Inventory Item ID', 'Short ID', 'Item Name', 'Item Type', 'Category', 'Subcategory',
  'Size', 'Unit', 'Supplier',
  'Opening Quantity', 'Received', 'Transfer In', 'Adjustment +', 'Sales / Depletion',
  'Waste', 'Transfer Out', 'Adjustment -', 'Reversal / Void',
  'Net Change', 'Closing Quantity',
  'Unit Cost', 'Opening Value', 'Closing Value', 'Selling Price',
];

/**
 * REPORTING.R5 — the by-location sheet.
 *
 * One row per (item, location), which is the shape a pivot table wants. The
 * Summary sheet keeps its existing columns untouched — people have formulas
 * against it — so this is an additional tab rather than extra columns.
 *
 * QUANTITIES ONLY. The server splits quantities per location and not value, so
 * there is no Unit Cost or Closing Value here. A blank column would invite
 * someone to fill it by apportioning, which would be an invention.
 */
const BY_LOCATION_HEADERS = [
  'Inventory Item ID', 'Short ID', 'Item Name', 'Category', 'Subcategory', 'Location',
  'Opening Quantity', 'Received', 'Transfer In', 'Adjustment +', 'Sales / Depletion',
  'Waste', 'Transfer Out', 'Adjustment -', 'Reversal / Void',
  'Net Change', 'Closing Quantity',
];

const MOVEMENT_HEADERS = [
  'Date', 'Inventory Item ID', 'Short ID', 'Item Name', 'Location', 'Movement Type', 'Bucket',
  'Quantity', 'Unit Cost', 'Total Cost', 'Reference Type', 'Reference ID',
  // POS-ORDER-NUMBER.1 — beside the id, never instead of it. Blank on every
  // row posted before the sender included one and on every reference type
  // that has no human number. Named as the register names it.
  'Order Number', 'Posted By', 'Notes',
];

/**
 * Human-readable export filename:
 *   inventory-movements-<location-name>-<from>-to-<to>.xlsx
 *   inventory-movements-<from>-to-<to>.xlsx   (venue-wide, no location filter)
 */
export function buildInventoryMovementsReportFilename(report: InventoryMovementsReport): string {
  const { meta } = report;
  const fromPart = fmtDateOnly(meta.from);
  const toPart = fmtDateOnly(meta.to);
  const locPart = meta.locationName ? sanitizeFilePart(meta.locationName) : '';
  return locPart
    ? `inventory-movements-${locPart}-${fromPart}-to-${toPart}.xlsx`
    : `inventory-movements-${fromPart}-to-${toPart}.xlsx`;
}

/** Build the workbook and trigger a client-side download. No backend calls. */
export function downloadInventoryMovementsReport(report: InventoryMovementsReport): void {
  const { meta, rows, movements } = report;
  const wb = XLSX.utils.book_new();

  // ── Sheet 1: Summary ──────────────────────────────────────────────────────
  const summaryAoa: (string | number)[][] = [
    SUMMARY_HEADERS,
    ...rows.map(r => [
      r.inventoryItemId,
      r.shortId ?? '',
      r.itemName ?? '',
      r.itemType ?? '',
      r.category ?? '',
      r.subcategory ?? '',
      r.sizeValue != null ? r.sizeValue : '',
      r.sizeUnit ?? '',
      r.supplierName ?? '',
      numOrBlank(r.openingQuantity),
      numOrBlank(r.received),
      numOrBlank(r.transferIn),
      numOrBlank(r.adjustmentPlus),
      numOrBlank(r.salesDepletion),
      numOrBlank(r.waste),
      numOrBlank(r.transferOut),
      numOrBlank(r.adjustmentMinus),
      numOrBlank(r.reversalVoid),
      numOrBlank(r.netChange),
      numOrBlank(r.closingQuantity),
      numOrBlank(fromMinorUnitsOrNull(r.unitCost)),
      numOrBlank(fromMinorUnitsOrNull(r.openingValue)),
      numOrBlank(fromMinorUnitsOrNull(r.closingValue)),
      numOrBlank(fromMinorUnitsOrNull(r.salePrice)),
    ]),
  ];
  const summaryWs = XLSX.utils.aoa_to_sheet(summaryAoa);
  summaryWs['!cols'] = SUMMARY_HEADERS.map(h => ({ wch: Math.max(h.length + 2, 12) }));
  XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

  // ── Sheet 2: By Location ──────────────────────────────────────────────────
  const byLocationAoa: (string | number)[][] = [
    BY_LOCATION_HEADERS,
    ...rows.flatMap(r => r.byLocation.map(l => [
      r.inventoryItemId,
      r.shortId ?? '',
      r.itemName ?? '',
      r.category ?? '',
      r.subcategory ?? '',
      l.locationName ?? '',
      numOrBlank(l.openingQuantity),
      numOrBlank(l.received),
      numOrBlank(l.transferIn),
      numOrBlank(l.adjustmentPlus),
      numOrBlank(l.salesDepletion),
      numOrBlank(l.waste),
      numOrBlank(l.transferOut),
      numOrBlank(l.adjustmentMinus),
      numOrBlank(l.reversalVoid),
      numOrBlank(l.netChange),
      numOrBlank(l.closingQuantity),
    ])),
  ];
  const byLocationWs = XLSX.utils.aoa_to_sheet(byLocationAoa);
  byLocationWs['!cols'] = BY_LOCATION_HEADERS.map(h => ({ wch: Math.max(h.length + 2, 12) }));
  XLSX.utils.book_append_sheet(wb, byLocationWs, 'By Location');

  // ── Sheet 3: Movement Detail ──────────────────────────────────────────────
  const movementAoa: (string | number)[][] = [
    MOVEMENT_HEADERS,
    ...(movements ?? []).map(m => [
      fmtDateTime(m.date),
      m.inventoryItemId,
      m.shortId ?? '',
      m.itemName ?? '',
      m.location ?? '',
      m.movementType,
      m.bucket ?? '',
      numOrBlank(m.quantity),
      numOrBlank(fromMinorUnitsOrNull(m.unitCost)),
      numOrBlank(fromMinorUnitsOrNull(m.totalCost)),
      m.sourceDocumentType ?? '',
      m.sourceDocumentId ?? '',
      m.sourceDocumentNumber ?? '',
      m.postedBy ?? '',
      m.notes ?? '',
    ]),
  ];
  if (meta.truncated) {
    movementAoa.push([`NOTE: detail truncated — showing the first ${(movements ?? []).length} of ${meta.movementCount} movements. Narrow the date range or filters for the full detail.`]);
  }
  const movementWs = XLSX.utils.aoa_to_sheet(movementAoa);
  movementWs['!cols'] = MOVEMENT_HEADERS.map(h => ({ wch: Math.max(h.length + 2, 12) }));
  XLSX.utils.book_append_sheet(wb, movementWs, 'Movement Detail');

  // ── Sheet 4: Metadata ─────────────────────────────────────────────────────
  const metaAoa: (string | number)[][] = [
    ['Field', 'Value'],
    ['Report Name', meta.reportName],
    ['Venue', meta.venueName ?? meta.venueId],
    ['Location Filter', meta.locationName ?? (meta.filters.locationId ?? 'All locations')],
    ['Category Filter', meta.filters.categoryId ?? ''],
    ['Subcategory Filter', meta.filters.subcategoryId ?? ''],
    ['Item Filter', meta.filters.itemId ?? ''],
    ['Period From', fmtDateTime(meta.from)],
    ['Period To', fmtDateTime(meta.to)],
    ['Opening Basis', meta.openingBasis],
    ['Cost Basis', meta.costBasis],
    ['Movements In Period', meta.movementCount],
    ['Detail Truncated', meta.truncated ? 'YES' : 'no'],
    ['Generated At', fmtDateTime(meta.generatedAt)],
    ...meta.notes.map((n, i) => [i === 0 ? 'Notes' : '', n]),
  ];
  const metaWs = XLSX.utils.aoa_to_sheet(metaAoa);
  metaWs['!cols'] = [{ wch: 26 }, { wch: 80 }];
  XLSX.utils.book_append_sheet(wb, metaWs, 'Metadata');

  XLSX.writeFile(wb, buildInventoryMovementsReportFilename(report));
}
